import { Currency } from '@ps/web-core-enums';
import { get, isNull, isUndefined } from 'lodash';

import type {
  Limits,
  LossLimitType,
  ResponsibleGamingLimitsPayload,
  TimeLimit
} from '../types/responsibleGamingLimits';
import { LossLimitValue } from '../types/responsibleGamingLimits';

import { convertAmountByRate, dollarsToCents } from './cashier';

const LIMIT_UNSET = -1;
const MIN_LIMIT_DEFAULT_VALUE = 1;
const MAX_LIMIT_DEFAULT_VALUE = 1000000;

const getLimitAmountByType = (limits: LossLimitType[], limitType: LossLimitValue): number =>
  get(
    limits?.find(({ type }) => type === limitType),
    'amount',
    LIMIT_UNSET
  );

const getLimitValue = (
  { limit, previousLimit }: ResponsibleGamingLimitsPayload['daily'],
  rate: number,
  currency: Currency
): number => {
  if (limit === -1) return limit;

  const amount =
    !isUndefined(limit) && currency !== Currency.USDollar && !isNull(rate)
      ? dollarsToCents(convertAmountByRate(Number(limit), rate, currency, false))
      : dollarsToCents(Number(limit));

  return amount || previousLimit;
};

export const getDepositLimitsPayload = ({
  currency,
  rate,
  daily = { limit: LIMIT_UNSET, previousLimit: LIMIT_UNSET },
  weekly = { limit: LIMIT_UNSET, previousLimit: LIMIT_UNSET },
  monthly = { limit: LIMIT_UNSET, previousLimit: LIMIT_UNSET }
}: ResponsibleGamingLimitsPayload) => ({
  dailyLimit: getLimitValue(daily, rate, currency),
  weeklyLimit: getLimitValue(weekly, rate, currency),
  monthlyLimit: getLimitValue(monthly, rate, currency),
  passwordRequired: false
});

const getLossLimit = (type: LossLimitValue, currency: Currency, limit?: number) => {
  if (!isUndefined(limit) || (limit && limit !== -1)) {
    return { amount: dollarsToCents(Number(limit)), type, currency };
  }

  return;
};

const getDepositLimitAmountRestriction = (timeLimit: TimeLimit) => ({
  limit: timeLimit.limit === -1 ? timeLimit.limit : timeLimit.limit / 100,
  limitMin: timeLimit.limitMin / 100,
  limitMax: timeLimit.limitMax / 100
});

const getLossLimitAmountRestriction = (limits: LossLimitType[], type: LossLimitValue) => ({
  limit: getLimitAmountByType(limits, type),
  limitMin: MIN_LIMIT_DEFAULT_VALUE,
  limitMax: MAX_LIMIT_DEFAULT_VALUE
});

export const getLossLimitsPayload = ({
  currency,
  daily,
  weekly,
  monthly
}: ResponsibleGamingLimitsPayload) =>
  [
    getLossLimit(LossLimitValue.Daily, currency, daily?.limit),
    getLossLimit(LossLimitValue.Weekly, currency, weekly?.limit),
    getLossLimit(LossLimitValue.Monthly, currency, monthly?.limit)
  ].filter((limit) => !isUndefined(limit)) as LossLimitType[];

export const getDepositLimitAmountRestrictions = ({ daily, weekly, monthly }: Limits) => ({
  daily: getDepositLimitAmountRestriction(daily),
  weekly: getDepositLimitAmountRestriction(weekly),
  monthly: getDepositLimitAmountRestriction(monthly)
});

export const getLossLimitAmountRestrictions = (limits: LossLimitType[]) => ({
  daily: getLossLimitAmountRestriction(limits, LossLimitValue.Daily),
  weekly: getLossLimitAmountRestriction(limits, LossLimitValue.Weekly),
  monthly: getLossLimitAmountRestriction(limits, LossLimitValue.Monthly)
});
